// src/hooks/useShortCodeHandlers.js
import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

/**
 * Base short code handler - provides common functionality
 * Each plot type can extend this with specific handling
 */
const useBaseShortCodeHandler = ({
  location,
  updateMultipleSettings,
  setSelectedFiles,
  resetGroups = null,
  setAnalysisMode = null,
  pageName = "Plot",
}) => {
  const [userPassedShortCode, setUserPassedShortCode] = useState(null);
  const userPassedShortCodeRef = useRef(null);

  const [autoRunPlot, setAutoRunPlot] = useState(false);
  const navigate = useNavigate();

  const setOneTimeShortCode = useCallback((shortCode) => {
    const value = shortCode || null;
    userPassedShortCodeRef.current = value;
    setUserPassedShortCode(value);
  }, []);

  const consumeUserPassedShortCode = useCallback(() => {
    const value = userPassedShortCodeRef.current;

    userPassedShortCodeRef.current = null;
    setUserPassedShortCode(null);

    return value || null;
  }, []);

  const handleShortCode = (
    shortCodePayload,
    originalShortCode,
    customHandler
  ) => {
    if (shortCodePayload && location.state?.isFromShortCode) {
      console.log(
        `Loading ${pageName} settings from shared link:`,
        shortCodePayload
      );

      const nextState = {
        ...location.state,
        shortCode: null,
        shortCodePayload: null,
        isFromShortCode: false,
        suppressDefaults: true,
      };

      navigate(`${location.pathname}${location.search}${location.hash}`, {
        replace: true,
        state: nextState,
      });

      setOneTimeShortCode(originalShortCode);

      // Call the custom handler for this plot type
      if (customHandler) {
        customHandler(shortCodePayload);
      }

      // Show success message
      toast.success(
        `${pageName} settings loaded from shared link - generating plot automatically...`
      );

      // Trigger auto-run after a short delay
      setTimeout(() => {
        setAutoRunPlot(true);
      }, 100);
    }
  };

  return {
    userPassedShortCode,
    consumeUserPassedShortCode,
    autoRunPlot,
    setAutoRunPlot,
    handleShortCode,
  };
};

/**
 * Subcodon Profile specific short code handler
 */
export const useSubcodonProfileShortCode = ({
  location,
  updateMultipleSettings,
  setSelectedFiles,
}) => {
  console.log("useSubcodon short code handler called")
  const { userPassedShortCode, 
          consumeUserPassedShortCode, 
          autoRunPlot, 
          setAutoRunPlot, 
          handleShortCode } =
    useBaseShortCodeHandler({
      location,
      updateMultipleSettings,
      setSelectedFiles,
      pageName: "Subcodon Profile",
    });

  useEffect(() => {
    const shortCodePayload = location.state?.shortCodePayload;
    const originalShortCode = location.state?.shortCode;

    // Only process if we have a short code payload and haven't processed it yet
    if (
      shortCodePayload &&
      location.state?.isFromShortCode &&
      !userPassedShortCode
    ) {
      const customHandler = (payload) => {
        console.log("customHandler called with payload", payload.transcript);
        // Subcodon Profile specific field mapping
        updateMultipleSettings({
          riboSeqCoverage: payload.cov_check || false,
          highlightStart: payload.hili_start || "",
          highlightStop: payload.hili_stop || "",
          maxRead: payload.maxread || 150,
          minRead: payload.minread || 20,
          offsetDirection: payload.primetype || "fiveprime",
          minTripletScore: parseFloat(payload.readscore) || 0,
          highlightSequences: payload.seqhili || "",
          transcript: payload.transcript || "",
        });

        // Handle file selection (simple file_list)
        if (payload.file_list && Array.isArray(payload.file_list)) {
          setSelectedFiles(payload.file_list);
        }
      };

      handleShortCode(shortCodePayload, originalShortCode, customHandler);
    }
  }, [
    location.state?.shortCodePayload,
    location.state?.isFromShortCode,
    userPassedShortCode,
  ]);

  return { userPassedShortCode, consumeUserPassedShortCode, autoRunPlot, setAutoRunPlot };
};

/**
 * RRTS specific short code handler
 */
export const useRRTSShortCode = ({
  location,
  updateMultipleSettings,
  setSelectedFiles,
  resetGroups,
  setAnalysisMode,
}) => {
  const { userPassedShortCode, 
          consumeUserPassedShortCode, 
          autoRunPlot, 
          setAutoRunPlot, 
          handleShortCode } =
    useBaseShortCodeHandler({
      location,
      updateMultipleSettings,
      setSelectedFiles,
      resetGroups,
      setAnalysisMode,
      pageName: "RRTS",
    });

  useEffect(() => {
    const shortCodePayload = location.state?.shortCodePayload;
    const originalShortCode = location.state?.shortCode;

    // Only process if we have a short code payload and haven't processed it yet
    if (
      shortCodePayload &&
      location.state?.isFromShortCode &&
      !userPassedShortCode
    ) {
      const customHandler = (payload) => {
        // Set analysis mode first
        if (setAnalysisMode && payload.analysis_type) {
          setAnalysisMode(payload.analysis_type);
        }

        // RRTS specific field mapping (updated to match actual payload structure)
        updateMultipleSettings({
          analysisType: payload.analysis_type || "single",
          ambiguousReads: payload.ambig_check || false,
          tranList: payload.tran_list || "prin_trans",
          customTranList: Array.isArray(payload.custom_tran_list)
            ? payload.custom_tran_list.join(",")
            : payload.custom_tran_list || "",
          excludeRtStop: payload.exclude_rt_stop_check || false,
          minRtCount: payload.min_rt_count || 5,
          contextBefore: Array.isArray(payload.context_before)
            ? payload.context_before.join(",")
            : payload.context_before || "",
          uaaCheck: payload.uaa_check || false,
          uagCheck: payload.uag_check || false,
          ugaCheck: payload.uga_check || false,
          contextAfter: Array.isArray(payload.context_after)
            ? payload.context_after.join(",")
            : payload.context_after || "",
          minCdsDensity: payload.min_cds_density || 50,
          minCdsCoverage: payload.min_cds_coverage || 0.5,
          minReadLength: payload.minread || 20,
          maxReadLength: payload.maxread || 150,
          minFoldChange: payload.min_fold_change || 2,
          windowSize: payload.window_size || 30,
          minCoverage: payload.min_coverage || 10,
          minNonZero: payload.min_non_zero || 0.5,
          nucOutput: payload.nuc_output || 10,
          emailNotification: payload.email || false,
        });

        // Handle file selection based on master_file_dict structure
        if (payload.master_file_dict) {
          if (payload.analysis_type === "single") {
            // For single mode, use files from group1 or the first available group
            const firstGroup =
              payload.master_file_dict.group1 ||
              payload.master_file_dict.Group_1 ||
              Object.values(payload.master_file_dict)[0];
            if (firstGroup && firstGroup.file_ids) {
              const fileIds = firstGroup.file_ids.map((id) => parseInt(id));
              setSelectedFiles(fileIds);
            }
          } else if (payload.analysis_type === "comparison") {
            console.log("Comparison mode detected, processing groups...");

            // For comparison mode, use addFilesToGroup instead of resetGroups
            // First clear existing groups
            if (resetGroups) {
              resetGroups([
                {
                  id: "group1",
                  name: payload.cond1_name || "Group 1",
                  files: [],
                },
                {
                  id: "group2",
                  name: payload.cond2_name || "Group 2",
                  files: [],
                },
              ]);
            }

            // Then add files to groups with a small delay to ensure groups are ready
            setTimeout(() => {
              // Handle group1/Group_1
              const group1Data =
                payload.master_file_dict.group1 ||
                payload.master_file_dict.Group_1;
              if (group1Data && group1Data.file_ids) {
                const fileIds = group1Data.file_ids.map((id) => parseInt(id));
                console.log("Adding files to group1:", fileIds);

                // Use the generic resetGroups to set both groups at once
                const newGroups = [
                  {
                    id: "group1",
                    name: payload.cond1_name || "Group 1",
                    files: fileIds,
                  },
                  {
                    id: "group2",
                    name: payload.cond2_name || "Group 2",
                    files: [],
                  },
                ];

                // Handle group2/Group_2
                const group2Data =
                  payload.master_file_dict.group2 ||
                  payload.master_file_dict.Group_2;
                if (group2Data && group2Data.file_ids) {
                  const group2FileIds = group2Data.file_ids.map((id) =>
                    parseInt(id)
                  );
                  console.log("Adding files to group2:", group2FileIds);
                  newGroups[1].files = group2FileIds;
                }

                console.log("Final groups structure:", newGroups);
                resetGroups(newGroups);
              }
            }, 100);
          }
        }
      };

      handleShortCode(shortCodePayload, originalShortCode, customHandler);
    }
  }, [
    location.state?.shortCodePayload,
    location.state?.isFromShortCode,
    userPassedShortCode,
  ]);

  return { userPassedShortCode, consumeUserPassedShortCode, autoRunPlot, setAutoRunPlot };
};

/**
 * Data Quality specific short code handler
 */
export const useDataQualityShortCode = ({
  location,
  updateMultipleSettings,
  setSelectedFiles,
}) => {
  const { userPassedShortCode, 
          consumeUserPassedShortCode, 
          autoRunPlot, 
          setAutoRunPlot, 
          handleShortCode } =
    useBaseShortCodeHandler({
      location,
      updateMultipleSettings,
      setSelectedFiles,
      pageName: "Data Quality",
    });

  useEffect(() => {
    const shortCodePayload = location.state?.shortCodePayload;
    const originalShortCode = location.state?.shortCode;

    // Only process if we have a short code payload and haven't processed it yet
    if (
      shortCodePayload &&
      location.state?.isFromShortCode &&
      !userPassedShortCode
    ) {
      const customHandler = (payload) => {
        // Data Quality specific field mapping
        const settingsToUpdate = {};

        // Only update plottype if it's provided in the payload
        if (payload.plottype) {
          settingsToUpdate.plottype = payload.plottype;
        }
        updateMultipleSettings({
          ...settingsToUpdate,
          minread: payload.minread ?? 20,
          maxread: payload.maxread ?? 35,
          minread_count: payload.minread_count ?? 10,
          // Add all other data quality specific fields
          min_cust_metagene_search_limit: payload.min_cust_metagene_search_limit ?? 0,
          max_cust_metagene_search_limit: payload.max_cust_metagene_search_limit ?? 0,
          min_limit_anchor: payload.min_limit_anchor ?? "cds_start",
          max_limit_anchor: payload.max_limit_anchor ?? "cds_start",
          hmap_strt: payload.hmap_strt ?? -50,
          hmap_stop: payload.hmap_stop ?? 20,
          max_y_val: payload.max_y_val ?? 0,
          maxscaleval: payload.maxscaleval ?? "None",
          custom_seq_list: payload.custom_seq_list ?? "",
          metagene_tranlist: payload.metagene_tranlist ?? "",
          te_genelist: payload.te_genelist ?? "",
          log_scale: payload.log_scale ?? false,
          reverse: payload.reverse ?? false,
          apply_offset: payload.apply_offset ?? true,
          per: payload.per ?? true,
          ambig: payload.ambig ?? true,
          single_plt: payload.single_plt ?? true,
          aggregate_counts: payload.aggregate_counts ?? true,
          custom_search_region: payload.custom_search_region ?? "whole_gene",
          nuc_comp_type: payload.nuc_comp_type ?? "per",
          direction: payload.direction ?? "fiveprime",
          nuccomp_reads: payload.nuccomp_reads ?? "mapped",
          center_around: payload.center_around ?? "start_codons",
          color_palette: payload.color_palette ?? "gist_rainbow",
          norm_method: payload.norm_method ?? "average_read_density",
          count_type: payload.count_type ?? "gene_counts",
          mrna_dist_category: payload.mrna_dist_category ?? "mrna_dist_file",
          corr_type: payload.corr_type ?? "pearson",
          count_result: payload.count_result ?? "raw_count",
          organism: payload.organism ?? "homo_sapiens",
          transcriptome:
            payload.transcriptome ?? "homo_sapiens.Gencode_v41.appris",
        });

        // Handle file selection (simple file_list)
        if (payload.file_list && Array.isArray(payload.file_list)) {
          setSelectedFiles(payload.file_list);
        }
      };

      handleShortCode(shortCodePayload, originalShortCode, customHandler);
    }
  }, [
    location.state?.shortCodePayload,
    location.state?.isFromShortCode,
    userPassedShortCode,
  ]);

  return { userPassedShortCode, consumeUserPassedShortCode, autoRunPlot, setAutoRunPlot };
};

/**
 * Batch Subcodon Profile specific short code handler
 */
export const useBatchSubcodonProfileShortCode = ({
  location,
  updateMultipleSettings,
  setSelectedFiles,
  updateBatchSetting,
}) => {
  const { userPassedShortCode, 
          consumeUserPassedShortCode, 
          autoRunPlot, 
          setAutoRunPlot, 
          handleShortCode } =
    useBaseShortCodeHandler({
      location,
      updateMultipleSettings,
      setSelectedFiles,
      pageName: "Batch Subcodon Profile",
    });

  useEffect(() => {
    const shortCodePayload = location.state?.shortCodePayload;
    const originalShortCode = location.state?.shortCode;

    // Only process if we have a short code payload and haven't processed it yet
    if (
      shortCodePayload &&
      location.state?.isFromShortCode &&
      !userPassedShortCode
    ) {
      const customHandler = (payload) => {
        // Batch Subcodon Profile specific field mapping
        updateMultipleSettings({
          riboSeqCoverage: payload.cov_check || false,
          highlightStart: payload.hili_start || "",
          highlightStop: payload.hili_stop || "",
          maxRead: payload.maxread || 150,
          minRead: payload.minread || 20,
          offsetDirection: payload.primetype || "fiveprime",
          minTripletScore: parseFloat(payload.readscore) || 0,
          highlightSequences: payload.seqhili || "",
          transcript: payload.transcript || "",
        });

        // Set batch-specific settings
        if (updateBatchSetting) {
          updateBatchSetting("csvCheck", payload.csv_check || false);
          updateBatchSetting("htmlCheck", payload.html_check || false);
          if (payload.transcript_list) {
            updateBatchSetting("transcriptList", payload.transcript_list);
          }
        }

        // Handle file selection (simple file_list)
        if (payload.file_list && Array.isArray(payload.file_list)) {
          setSelectedFiles(payload.file_list);
        }
      };

      handleShortCode(shortCodePayload, originalShortCode, customHandler);
    }
  }, [
    location.state?.shortCodePayload,
    location.state?.isFromShortCode,
    userPassedShortCode,
    updateBatchSetting,
  ]);

  return { userPassedShortCode, consumeUserPassedShortCode, autoRunPlot, setAutoRunPlot };
};

/**
 * Comparison Profile specific short code handler
 */
export const useComparisonProfileShortCode = ({
  location,
  updateMultipleSettings,
  clearAllGroups,
  addFilesToGroup,
  updateGroupName,
  updateGroupColor,
  setIsBatchMode,
}) => {
  const { userPassedShortCode, 
          consumeUserPassedShortCode, 
          autoRunPlot, 
          setAutoRunPlot, 
          handleShortCode } =
    useBaseShortCodeHandler({
      location,
      updateMultipleSettings,
      setSelectedFiles: () => {}, // Not used for comparison profile
      pageName: "Comparison Profile",
    });

  useEffect(() => {
    const shortCodePayload = location.state?.shortCodePayload;
    const originalShortCode = location.state?.shortCode;

    // Only process if we have a short code payload and haven't processed it yet
    if (
      shortCodePayload &&
      location.state?.isFromShortCode &&
      !userPassedShortCode
    ) {
      const customHandler = (payload) => {
        // Comparison Profile specific field mapping
        updateMultipleSettings({
          riboSeqCoverage: payload.cov_check || false,
          highlightStart: payload.hili_start || "",
          highlightStop: payload.hili_stop || "",
          maxRead: payload.maxread || 150,
          minRead: payload.minread || 20,
          normalizationType: payload.norm_type || "by_type",
          transcript: payload.transcript || "",
        });

        // Handle batch mode if specified
        if (payload.is_batch_mode !== undefined && setIsBatchMode) {
          setIsBatchMode(payload.is_batch_mode);
        }

        // Handle groups from master_file_dict (ComparisonProfile specific)
        if (payload.master_file_dict && clearAllGroups && addFilesToGroup) {
          // Clear all existing groups first
          clearAllGroups();

          // Process groups from master_file_dict
          const groupEntries = Object.entries(payload.master_file_dict);
          groupEntries.forEach(([groupKey, groupData], index) => {
            if (groupData.file_ids && Array.isArray(groupData.file_ids)) {

              const files = groupData.file_ids.map((id) => parseInt(id, 10));
              const groupId = index + 1; // Groups have numeric IDs starting from 1


              // Add files to the corresponding group
              addFilesToGroup(groupId, files);


              const group_label = groupData.label
              updateGroupName(groupId, group_label);

              const group_color = groupKey
              updateGroupColor(groupId, group_color);
            }
          });
        }
      };

      handleShortCode(shortCodePayload, originalShortCode, customHandler);
    }
  }, [
    location.state?.shortCodePayload,
    location.state?.isFromShortCode,
    userPassedShortCode,
    setIsBatchMode,
  ]);

  return { userPassedShortCode, consumeUserPassedShortCode, autoRunPlot, setAutoRunPlot };
};

/**
 * Batch Comparison Profile specific short code handler
 */
export const useBatchComparisonProfileShortCode = ({
  location,
  updateMultipleSettings,
  clearAllGroups,
  addFilesToGroup,
  updateBatchSetting,
}) => {
  const { userPassedShortCode, 
          consumeUserPassedShortCode, 
          autoRunPlot, 
          setAutoRunPlot, 
          handleShortCode } =
    useBaseShortCodeHandler({
      location,
      updateMultipleSettings,
      setSelectedFiles: () => {}, // Not used for comparison profile
      pageName: "Batch Comparison Profile",
    });

  useEffect(() => {
    const shortCodePayload = location.state?.shortCodePayload;
    const originalShortCode = location.state?.shortCode;

    // Only process if we have a short code payload and haven't processed it yet
    if (
      shortCodePayload &&
      location.state?.isFromShortCode &&
      !userPassedShortCode
    ) {
      const customHandler = (payload) => {
        // Batch Comparison Profile specific field mapping
        updateMultipleSettings({
          riboSeqCoverage: payload.cov_check || false,
          highlightStart: payload.hili_start || "",
          highlightStop: payload.hili_stop || "",
          maxRead: payload.maxread || 150,
          minRead: payload.minread || 20,
          normalizationType: payload.norm_type || "by_type",
          transcript: payload.transcript || "",
        });

        // Set batch-specific settings
        if (updateBatchSetting) {
          updateBatchSetting("csvCheck", payload.csv_check || false);
          updateBatchSetting("htmlCheck", payload.html_check || false);
          if (payload.transcript_list) {
            updateBatchSetting("transcriptList", payload.transcript_list);
          }
        }

        // Handle groups from master_file_dict (ComparisonProfile specific)
        if (payload.master_file_dict && clearAllGroups && addFilesToGroup) {
          // Clear all existing groups first
          clearAllGroups();

          // Process groups from master_file_dict
          const groupEntries = Object.entries(payload.master_file_dict);
          groupEntries.forEach(([groupKey, groupData], index) => {
            if (groupData.file_ids && Array.isArray(groupData.file_ids)) {
              const files = groupData.file_ids.map((id) => parseInt(id, 10));
              const groupId = index + 1; // Groups have numeric IDs starting from 1

              // Add files to the corresponding group
              addFilesToGroup(groupId, files);
            }
          });
        }
      };

      handleShortCode(shortCodePayload, originalShortCode, customHandler);
    }
  }, [
    location.state?.shortCodePayload,
    location.state?.isFromShortCode,
    userPassedShortCode,
    updateBatchSetting,
  ]);

  return { userPassedShortCode, consumeUserPassedShortCode, autoRunPlot, setAutoRunPlot };
};

/**
 * Pause Detection specific short code handler
 */
export const usePauseDetectionShortCode = ({
  location,
  updateMultipleSettings,
  setSelectedFiles,
  resetGroups,
  setAnalysisMode,
}) => {
  const { userPassedShortCode,
          consumeUserPassedShortCode, 
          autoRunPlot, 
          setAutoRunPlot, 
          handleShortCode } =
    useBaseShortCodeHandler({
      location,
      updateMultipleSettings,
      setSelectedFiles,
      pageName: "Pause Detection",
    });

  useEffect(() => {
    const shortCodePayload = location.state?.shortCodePayload;
    const originalShortCode = location.state?.shortCode;

    // Only process if we have a short code payload and haven't processed it yet
    if (
      shortCodePayload &&
      location.state?.isFromShortCode &&
      !userPassedShortCode
    ) {
      const customHandler = (payload) => {
        // Pause Detection specific field mapping
        updateMultipleSettings({
          minread: payload.minread ?? 20,
          maxread: payload.maxread ?? 35,
          tran_list: payload.tran_list ?? "prin_trans",
          custom_tran_list: payload.custom_tran_list ?? "",
          min_fold_change: payload.min_fold_change ?? 10,
          window_size: payload.window_size ?? 1000,
          min_coverage: payload.min_coverage ?? 5,
          min_non_zero: payload.min_non_zero ?? 20,
          nuc_output: payload.nuc_output ?? 15,
          reproducible: payload.reproducible ?? false,
          ambig_check: payload.ambig_check ?? false,
        });

        // Handle analysis mode
        if (payload.analysis_type && setAnalysisMode) {
          setAnalysisMode(payload.analysis_type);
        }

        // Handle groups for comparison mode (same as RRTS)
        if (payload.master_file_dict) {
          if (payload.analysis_type === "single") {
            // For single mode, use files from group1 or the first available group
            const firstGroup =
              payload.master_file_dict.group1 ||
              payload.master_file_dict.Group_1 ||
              Object.values(payload.master_file_dict)[0];
            if (firstGroup && firstGroup.file_ids) {
              const fileIds = firstGroup.file_ids.map((id) => parseInt(id));
              setSelectedFiles(fileIds);
            }
          } else if (payload.analysis_type === "comparison") {
            console.log("Comparison mode detected, processing groups...");

            // For comparison mode, use addFilesToGroup instead of resetGroups
            // First clear existing groups
            if (resetGroups) {
              resetGroups([
                {
                  id: "group1",
                  name: payload.cond1_name || "Group 1",
                  files: [],
                },
                {
                  id: "group2",
                  name: payload.cond2_name || "Group 2",
                  files: [],
                },
              ]);
            }

            // Then add files to groups with a small delay to ensure groups are ready
            setTimeout(() => {
              // Handle group1/Group_1
              const group1Data =
                payload.master_file_dict[payload.cond1_name] ||
                payload.master_file_dict.Group_1;
              if (group1Data && group1Data.file_ids) {
                const fileIds = group1Data.file_ids.map((id) => parseInt(id));
                console.log("Adding files to group1:", fileIds);

                // Use the generic resetGroups to set both groups at once
                const newGroups = [
                  {
                    id: "group1",
                    name: payload.cond1_name || "Group 1",
                    files: fileIds,
                  },
                  {
                    id: "group2",
                    name: payload.cond2_name || "Group 2",
                    files: [],
                  },
                ];

                // Handle group2/Group_2
                const group2Data =
                  payload.master_file_dict[payload.cond2_name] ||
                  payload.master_file_dict.Group_2;
                if (group2Data && group2Data.file_ids) {
                  const group2FileIds = group2Data.file_ids.map((id) =>
                    parseInt(id)
                  );
                  console.log("Adding files to group2:", group2FileIds);
                  newGroups[1].files = group2FileIds;
                }

                console.log("Final groups structure:", newGroups);
                resetGroups(newGroups);
              }
            }, 100);
          }
        }
      };

      handleShortCode(shortCodePayload, originalShortCode, customHandler);
    }
  }, [
    location.state?.shortCodePayload,
    location.state?.isFromShortCode,
    userPassedShortCode,
    setAnalysisMode,
  ]);

  return { userPassedShortCode, consumeUserPassedShortCode, autoRunPlot, setAutoRunPlot };
};

/**
 * Translon Explorer specific short code handler
 */
export const useTranslonExplorerShortCode = ({
  location,
  updateMultipleSettings,
  setSelectedFiles,
}) => {
  const { userPassedShortCode,
          consumeUserPassedShortCode, 
          autoRunPlot, 
          setAutoRunPlot, 
          handleShortCode } =
    useBaseShortCodeHandler({
      location,
      updateMultipleSettings,
      setSelectedFiles,
      pageName: "Translon Explorer",
    });

  useEffect(() => {
    const shortCodePayload = location.state?.shortCodePayload;
    const originalShortCode = location.state?.shortCode;

    // Only process if we have a short code payload and haven't processed it yet
    if (
      shortCodePayload &&
      location.state?.isFromShortCode &&
      !userPassedShortCode
    ) {
      const customHandler = (payload) => {
        // Translon Explorer specific field mapping
        updateMultipleSettings({

          orfType: payload.region || "uorf",
          transcriptInput: payload.tran_input || "prin_trans",
          customTranscriptList: payload.custom_tran_input || "",
          startCodons: payload.start_codons.map(codon => codon.replaceAll("T", "U")),
          features: {
            startIncreaseCheck: Boolean(payload.start_increase_check),
            stopDecreaseCheck: Boolean(payload.stop_decrease_check),
            coverageCheck: Boolean(payload.coverage_check),
            lowestFrameDiffCheck: Boolean(payload.lowest_frame_diff_check),
            highestFrameDiffCheck: Boolean(payload.highest_frame_diff_check),
            minStartIncrease: String(payload.min_start_increase || ""),
            minStopDecrease: String(payload.min_stop_decrease || ""),
            minCoverage: String(payload.min_coverage || ""),
            minLowestFrameDiff: String(payload.min_lowest_frame_diff || ""),
            minHighestFrameDiff: String(payload.min_highest_frame_diff || ""),
          },
          filters: {
            minProb: String(payload.min_prob || ""),
            maxProb: String(payload.max_prob || ""),
            minArd: String(payload.min_ard || ""),
            maxArd: String(payload.max_ard || ""),
            minCds: String(payload.min_cds || ""),
            maxCds: String(payload.max_cds || ""),
            minLen: String(payload.min_len || ""),
            maxLen: String(payload.max_len || ""),
            minReadLen: String(payload.min_readlen || ""),
            maxReadLen: String(payload.max_readlen || ""),
          },
          emailNotification: Boolean(payload.email),
        });

        // Handle file selection
        if (payload.file_list && Array.isArray(payload.file_list)) {
          setSelectedFiles(payload.file_list);
        }
      };

      handleShortCode(shortCodePayload, originalShortCode, customHandler);
    }
  }, [
    location.state?.shortCodePayload,
    location.state?.isFromShortCode,
    userPassedShortCode,
  ]);

  return { userPassedShortCode, consumeUserPassedShortCode, autoRunPlot, setAutoRunPlot };
};

/**
 * Differential Silhouette specific short code handler
 */
export const useDifferentialSilhouetteShortCode = ({
  location,
  updateMultipleSettings,
  resetGroups,
}) => {
  const { userPassedShortCode,  
          consumeUserPassedShortCode, 
          autoRunPlot, 
          setAutoRunPlot, 
          handleShortCode } =
    useBaseShortCodeHandler({
      location,
      updateMultipleSettings,
      setSelectedFiles: () => {}, // Not used for group-based analysis
      pageName: "Differential Silhouette",
    });

  useEffect(() => {
    const shortCodePayload = location.state?.shortCodePayload;
    const originalShortCode = location.state?.shortCode;

    // Only process if we have a short code payload and haven't processed it yet
    if (
      shortCodePayload &&
      location.state?.isFromShortCode &&
      !userPassedShortCode
    ) {
      const customHandler = (payload) => {
        // Differential Silhouette specific field mapping
        updateMultipleSettings({
          plotType: payload.plot_type || "both",
          geneType: payload.genetype || "coding",
          region: payload.region || "region_entire_tran",
          minReadLength: payload.minread || 20,
          maxReadLength: payload.maxread || 150,
          dMaxThreshold: payload.dmax_threshold || 0,
          dCorrThreshold: payload.dcorr_threshold || 0,
          dCorrMin: payload.dcorr_min || 0,
          ambiguousReads: payload.ambig_check || false,
          emailNotification: payload.email || false,
        });

        // Handle groups from master_file_dict (same as RRTS pattern)
        if (payload.master_file_dict && resetGroups) {
          console.log("Processing master_file_dict:", payload.master_file_dict);

          // Clear existing groups first
          resetGroups([]);

          // Process groups from master_file_dict
          const newGroups = [];
          Object.entries(payload.master_file_dict).forEach(
            ([groupKey, groupData], index) => {
              console.log(`Group ${index + 1} data:`, groupData);

              if (groupData.file_ids && Array.isArray(groupData.file_ids)) {
                const files = groupData.file_ids.map((id) => parseInt(id, 10));
                newGroups.push({
                  id: `group${index + 1}`,
                  name: groupKey,
                  files,
                });
              }
            }
          );

          console.log("Setting up groups:", newGroups);
          if (newGroups.length > 0) {
            resetGroups(newGroups);
          }
        }
      };

      handleShortCode(shortCodePayload, originalShortCode, customHandler);
    }
  }, [
    location.state?.shortCodePayload,
    location.state?.isFromShortCode,
    userPassedShortCode,
  ]);

  return { userPassedShortCode, consumeUserPassedShortCode, autoRunPlot, setAutoRunPlot };
};

/**
 * Differential CDS Leader specific short code handler
 */
export const useDifferentialCdsLeaderShortCode = ({
  location,
  updateMultipleSettings,
  resetGroups,
}) => {
  const { userPassedShortCode, 
          consumeUserPassedShortCode, 
          autoRunPlot, 
          setAutoRunPlot, 
          handleShortCode } =
    useBaseShortCodeHandler({
      location,
      updateMultipleSettings,
      setSelectedFiles: () => {}, // Not used for group-based analysis
      pageName: "Differential CDS Leader",
    });

  useEffect(() => {
    const shortCodePayload = location.state?.shortCodePayload;
    const originalShortCode = location.state?.shortCode;

    // Only process if we have a short code payload and haven't processed it yet
    if (
      shortCodePayload &&
      location.state?.isFromShortCode &&
      !userPassedShortCode
    ) {
      const customHandler = (payload) => {
        // Differential CDS Leader specific field mapping
        updateMultipleSettings({
          minread: payload.minread ?? 20,
          maxread: payload.maxread ?? 150,
          ambig_check: payload.ambig_check ?? false,
        });

        // Handle groups from master_file_dict (same as RRTS pattern)
        if (payload.master_file_dict && resetGroups) {
          console.log("Processing master_file_dict:", payload.master_file_dict);

          // Clear existing groups first
          resetGroups([]);

          // Process groups from master_file_dict
          const newGroups = [];
          Object.entries(payload.master_file_dict).forEach(
            ([groupKey, groupData], index) => {
              console.log(`Group ${index + 1} data:`, groupData);

              if (groupData.file_ids && Array.isArray(groupData.file_ids)) {
                const files = groupData.file_ids.map((id) => parseInt(id, 10));
                newGroups.push({
                  id: `group${index + 1}`,
                  name: groupKey,
                  files,
                });
              }
            }
          );

          console.log("Setting up groups:", newGroups);
          if (newGroups.length > 0) {
            resetGroups(newGroups);
          }
        }
      };

      handleShortCode(shortCodePayload, originalShortCode, customHandler);
    }
  }, [
    location.state?.shortCodePayload,
    location.state?.isFromShortCode,
    userPassedShortCode,
  ]);

  return { userPassedShortCode, consumeUserPassedShortCode, autoRunPlot, setAutoRunPlot };
};

/**
 * Codon Occupancy specific short code handler
 */


export const useCodonOccupancyShortCode = ({
  location,
  updateMultipleSettings,
  setSelectedFiles,
  resetGroups,
  setAnalysisMode,
}) => {
  const { userPassedShortCode, 
          consumeUserPassedShortCode,
          autoRunPlot, 
          setAutoRunPlot, 
          handleShortCode } =
    useBaseShortCodeHandler({
      location,
      updateMultipleSettings,
      setSelectedFiles: () => {}, // Not used for group-based analysis
      pageName: "Codon Occupancy",
    });

  useEffect(() => {
    const shortCodePayload = location.state?.shortCodePayload;
    const originalShortCode = location.state?.shortCode;

    // Only process if we have a short code payload and haven't processed it yet
    if (
      shortCodePayload &&
      location.state?.isFromShortCode &&
      !userPassedShortCode
    ) {
      const customHandler = (payload) => {
        // Codon Occupancy specific field mapping
        updateMultipleSettings({
          groupType: payload.group_type || "codon",
          normType: payload.norm_type || "average_read_density",
          minRead: payload.minread || 20,
          maxRead: payload.maxread || 35,
          xmin: payload.xmin || "",
          xmax: payload.xmax || "",
          ymin: payload.ymin || "",
          ymax: payload.ymax || "",
          tranList: payload.tran_list || "",
          emailNotification: payload.email || false,
        });

        // Handle analysis mode
        if (payload.plot_type && setAnalysisMode) {
          setAnalysisMode(payload.plot_type);
        }

        // Handle groups based on analysis mode (same as RRTS pattern)
        if (
          payload.plot_type === "comparison" &&
          payload.master_file_dict &&
          resetGroups
        ) {
          // Build groups deterministically using explicit keys to avoid ordering issues
          const group1Data =
            payload.master_file_dict.group1 || payload.master_file_dict.Group_1;
          const group2Data =
            payload.master_file_dict.group2 || payload.master_file_dict.Group_2;

          const toNumberArray = (arr) =>
            Array.isArray(arr)
              ? arr.map((id) => parseInt(id, 10)).filter((n) => !isNaN(n))
              : [];

          const group1FileIds = toNumberArray(group1Data?.file_ids);
          const group2FileIds = toNumberArray(group2Data?.file_ids);

          const newGroups = [
            {
              id: "group1",
              name: payload.cond1_name || "Group 1",
              files: group1FileIds,
            },
            {
              id: "group2",
              name: payload.cond2_name || "Group 2",
              files: group2FileIds,
            },
          ];

          // Defer group reset to the next tick to avoid race with analysis mode switch/render
          setTimeout(() => {
            resetGroups(newGroups);
          }, 0);
        } else if (
          payload.plot_type === "single" &&
          payload.master_file_dict
        ) {

            // For single mode, use files from group1 or the first available group
            const firstGroup =
              payload.master_file_dict.group1 ||
              payload.master_file_dict.Group_1 ||
              Object.values(payload.master_file_dict)[0];
            if (firstGroup && firstGroup.file_ids) {
              const fileIds = firstGroup.file_ids.map((id) => parseInt(id));
              setSelectedFiles(fileIds);
            }
          


        }
      };

      handleShortCode(shortCodePayload, originalShortCode, customHandler);
    }
  }, [
    location.state?.shortCodePayload,
    location.state?.isFromShortCode,
    userPassedShortCode,
    setAnalysisMode,
  ]);

  return { userPassedShortCode, consumeUserPassedShortCode, autoRunPlot, setAutoRunPlot };
};

/**
 * UTR Periodicity specific short code handler
 */
export const useUTRPeriodicityShortCode = ({
  location,
  updateMultipleSettings,
  resetGroups,
}) => {
  const { userPassedShortCode, 
          consumeUserPassedShortCode, 
          autoRunPlot, 
          setAutoRunPlot, 
          handleShortCode } =
    useBaseShortCodeHandler({
      location,
      updateMultipleSettings,
      setSelectedFiles: () => {}, // Not used for group-based analysis
      pageName: "UTR Periodicity",
    });

  useEffect(() => {
    const shortCodePayload = location.state?.shortCodePayload;
    const originalShortCode = location.state?.shortCode;

    // Only process if we have a short code payload and haven't processed it yet
    if (
      shortCodePayload &&
      location.state?.isFromShortCode &&
      !userPassedShortCode
    ) {
      const customHandler = (payload) => {
        // UTR Periodicity specific field mapping
        updateMultipleSettings({
          customTranList: payload.custom_tran_list || "",
          email: payload.email || false,
          excludeRtStop: payload.exclude_rt_stop || false,
          maxYVal: payload.max_y_val || null,
          maxread: payload.maxread || 150,
          minread: payload.minread || 20,
          selectRtOnly: payload.select_rt_only || false,
          tranSelection: payload.tran_selection || "principal",
        });

        // Handle groups from master_file_dict (same pattern as other group-based pages)
        if (payload.master_file_dict && resetGroups) {
          console.log("Processing master_file_dict:", payload.master_file_dict);

          // Clear existing groups first
          resetGroups([]);

          // Process groups from master_file_dict
          const newGroups = [];
          Object.entries(payload.master_file_dict).forEach(
            ([groupKey, groupData], index) => {
              console.log(`Group ${index + 1} data:`, groupData);

              if (groupData.file_ids && Array.isArray(groupData.file_ids)) {
                const files = groupData.file_ids.map((id) => parseInt(id, 10));
                newGroups.push({
                  id: `group${index + 1}`,
                  name: groupKey,
                  files,
                });
              }
            }
          );

          console.log("Setting up groups:", newGroups);
          if (newGroups.length > 0) {
            resetGroups(newGroups);
          }
        }
      };

      handleShortCode(shortCodePayload, originalShortCode, customHandler);
    }
  }, [
    location.state?.shortCodePayload,
    location.state?.isFromShortCode,
    userPassedShortCode,
  ]);

  return { userPassedShortCode, 
           consumeUserPassedShortCode, 
           autoRunPlot, 
           setAutoRunPlot };
};

/**
 * Termination Metagene specific short code handler
 */
export const useTerminationMetageneShortCode = ({
  location,
  updateMultipleSettings,
  resetGroups,
}) => {
  const { userPassedShortCode,
          consumeUserPassedShortCode, 
          autoRunPlot, 
          setAutoRunPlot, 
          handleShortCode } =
    useBaseShortCodeHandler({
      location,
      updateMultipleSettings,
      setSelectedFiles: () => {}, // Not used for group-based analysis
      pageName: "Termination Metagene",
    });

  useEffect(() => {
    const shortCodePayload = location.state?.shortCodePayload;
    const originalShortCode = location.state?.shortCode;

    // Only process if we have a short code payload and haven't processed it yet
    if (
      shortCodePayload &&
      location.state?.isFromShortCode &&
      !userPassedShortCode
    ) {
      const customHandler = (payload) => {
        // Termination Metagene specific field mapping
        updateMultipleSettings({
          customTranList: payload.custom_tran_list || "",
          downstreamSelection: payload.downstream_selection || "3utr",
          email: payload.email || false,
          maxread: payload.maxread || 150,
          minCount: payload.min_count || 10,
          minUtr: payload.min_utr || 50,
          minread: payload.minread || 20,
          normaliseMethod: payload.normalise_method || "ard",
          tranSelection: payload.tran_selection || "principal",
        });

        // Handle groups from master_file_dict (same pattern as other group-based pages)
        if (payload.master_file_dict && resetGroups) {
          console.log("Processing master_file_dict:", payload.master_file_dict);

          // Clear existing groups first
          resetGroups([]);

          // Process groups from master_file_dict
          const newGroups = [];
          Object.entries(payload.master_file_dict).forEach(
            ([groupKey, groupData], index) => {
              console.log(`Group ${index + 1} data:`, groupData);

              if (groupData.file_ids && Array.isArray(groupData.file_ids)) {
                const files = groupData.file_ids.map((id) => parseInt(id, 10));
                newGroups.push({
                  id: `group${index + 1}`,
                  name: groupKey,
                  files,
                });
              }
            }
          );

          console.log("Setting up groups:", newGroups);
          if (newGroups.length > 0) {
            resetGroups(newGroups);
          }
        }
      };

      handleShortCode(shortCodePayload, originalShortCode, customHandler);
    }
  }, [
    location.state?.shortCodePayload,
    location.state?.isFromShortCode,
    userPassedShortCode,
  ]);

  return { userPassedShortCode, consumeUserPassedShortCode, autoRunPlot, setAutoRunPlot };
};

/**
 * TimeSeries specific short code handler
 */
export const useTimeSeriesShortCode = ({
  location,
  updateMultipleSettings,
  setTimepoints,
  setCurrentMaxVisibleTimepoint,
}) => {
  const { userPassedShortCode, 
          consumeUserPassedShortCode, 
          autoRunPlot, 
          setAutoRunPlot, 
          handleShortCode } =
    useBaseShortCodeHandler({
      location,
      updateMultipleSettings,
      setSelectedFiles: () => {}, // Not used for timepoint-based analysis
      pageName: "TimeSeries",
    });

  useEffect(() => {
    const shortCodePayload = location.state?.shortCodePayload;
    const originalShortCode = location.state?.shortCode;

    // Only process if we have a short code payload and haven't processed it yet
    if (
      shortCodePayload &&
      location.state?.isFromShortCode &&
      !userPassedShortCode
    ) {
      const customHandler = (payload) => {
        // TimeSeries specific field mapping
        updateMultipleSettings({
          geneType: payload.genetype || "all",
          geneNo: payload.gene_no || 50,
          analysisType: payload.analysis_type || "single_control",
          riboRegion: payload.ribo_region || "all",
          rnaRegion: payload.rna_region || "all",
          selectedSeqType: payload.selected_seq_type || "TE",
          customControlName: payload.custom_control_name || "Control",
          customTreatmentName: payload.custom_treatment_name || "Treatment",
          colMin: payload.col_min || "",
          colMax: payload.col_max || "",
          customGeneList: payload.custom_gene_list || "",
          emailNotification: payload.email || false,
        });

        // Handle timepoints from master_file_dict
        if (
          payload.master_file_dict &&
          setTimepoints &&
          setCurrentMaxVisibleTimepoint
        ) {
          console.log(
            "Processing master_file_dict for timepoints:",
            payload.master_file_dict
          );

          const newTimepoints = [];
          let maxTimepointIndex = 0;

          // Process each timepoint from master_file_dict
          Object.entries(payload.master_file_dict).forEach(
            ([timepointKey, timepointData]) => {
              console.log(
                `Processing timepoint ${timepointKey}:`,
                timepointData
              );

              // Extract timepoint index from key like "time_0", "time_1", etc.
              const timepointMatch = timepointKey.match(/time_(\d+)/);
              if (timepointMatch) {
                const index = parseInt(timepointMatch[1], 10);
                maxTimepointIndex = Math.max(maxTimepointIndex, index);

                // Create timepoint object with proper structure
                const timepoint = {
                  groupName: timepointKey,
                  controlRiboFiles: timepointData.control_ribo_file_ids
                    ? timepointData.control_ribo_file_ids.map((id) =>
                        parseInt(id, 10)
                      )
                    : [],
                  controlRnaFiles: timepointData.control_rna_file_ids
                    ? timepointData.control_rna_file_ids.map((id) =>
                        parseInt(id, 10)
                      )
                    : [],
                  treatRiboFiles: timepointData.treat_ribo_file_ids
                    ? timepointData.treat_ribo_file_ids.map((id) =>
                        parseInt(id, 10)
                      )
                    : [],
                  treatRnaFiles: timepointData.treat_rna_file_ids
                    ? timepointData.treat_rna_file_ids.map((id) =>
                        parseInt(id, 10)
                      )
                    : [],
                };

                // Ensure array is large enough
                while (newTimepoints.length <= index) {
                  newTimepoints.push({
                    groupName: `time_${newTimepoints.length}`,
                    controlRiboFiles: [],
                    controlRnaFiles: [],
                    treatRiboFiles: [],
                    treatRnaFiles: [],
                  });
                }

                newTimepoints[index] = timepoint;
              }
            }
          );

          console.log("Setting up timepoints:", newTimepoints);
          console.log("Max timepoint index:", maxTimepointIndex);

          if (newTimepoints.length > 0) {
            setTimepoints(newTimepoints);
            setCurrentMaxVisibleTimepoint(maxTimepointIndex);
          }
        }
      };

      handleShortCode(shortCodePayload, originalShortCode, customHandler);
    }
  }, [
    location.state?.shortCodePayload,
    location.state?.isFromShortCode,
    userPassedShortCode,
  ]);

  return { userPassedShortCode, consumeUserPassedShortCode, autoRunPlot, setAutoRunPlot };
};

/**
 * PolyShift specific short code handler
 */
export const usePolyShiftShortCode = ({
  location,
  updateMultipleSettings,
  loadTimepointData,
}) => {
  const { userPassedShortCode,
          consumeUserPassedShortCode, 
          autoRunPlot, 
          setAutoRunPlot, 
          handleShortCode } =
    useBaseShortCodeHandler({
      location,
      updateMultipleSettings,
      setSelectedFiles: () => {}, // Not used for timepoint-based analysis
      pageName: "PolyShift",
    });

  useEffect(() => {
    const shortCodePayload = location.state?.shortCodePayload;
    const originalShortCode = location.state?.shortCode;

    // Only process if we have a short code payload and haven't processed it yet
    if (
      shortCodePayload &&
      location.state?.isFromShortCode &&
      !userPassedShortCode
    ) {
      const customHandler = (payload) => {
        // PolyShift specific field mapping
        updateMultipleSettings({
          plotType: payload.plot_type || "interactive",
          geneType: payload.genetype || "all",
          colorPalette: payload.selected_color_palette || "orange_purple",
          geneNo: payload.gene_no || 50,
          customControlName: payload.custom_control_name || "Control",
          customTreatmentName: payload.custom_treatment_name || "Treatment",
          customTranscriptList: payload.custom_transcript_list || "",
          zscoreCheck: payload.zscore_check || false,
          onlySigCheck: payload.only_sig_check || false,
          colMin: payload.col_min || "",
          colMax: payload.col_max || "",
          emailNotification: payload.email || false,
          plotUrl: payload.plot_url || "",
        });

        // Handle timepoints from master_file_dict using existing loadTimepointData function
        if (payload.master_file_dict && loadTimepointData) {
          console.log(
            "Processing master_file_dict for PolyShift timepoints:",
            payload.master_file_dict
          );
          loadTimepointData(payload.master_file_dict);
        }
      };

      handleShortCode(shortCodePayload, originalShortCode, customHandler);
    }
  }, [
    location.state?.shortCodePayload,
    location.state?.isFromShortCode,
    userPassedShortCode,
  ]);

  return { userPassedShortCode, consumeUserPassedShortCode, autoRunPlot, setAutoRunPlot };
};

/**
 * TranscriptomeInfo specific short code handler
 */
export const useTranscriptomeInfoShortCode = ({
  location,
  updateMultipleSettings,
  defaultTranscript,
}) => {
  const { userPassedShortCode, 
          consumeUserPassedShortCode,
          autoRunPlot, 
          setAutoRunPlot, 
          handleShortCode } =
    useBaseShortCodeHandler({
      location,
      updateMultipleSettings,
      setSelectedFiles: () => {}, // Not used for settings-only analysis
      pageName: "TranscriptomeInfo",
    });

  useEffect(() => {
    const shortCodePayload = location.state?.shortCodePayload;
    const originalShortCode = location.state?.shortCode;

    // Only process if we have a short code payload and haven't processed it yet
    if (
      shortCodePayload &&
      location.state?.isFromShortCode &&
      !userPassedShortCode
    ) {
      const customHandler = (payload) => {
        // TranscriptomeInfo specific field mapping
        updateMultipleSettings({
          plotType: payload.plottype || "",
          gcLocation: payload.gc_location || "all",
          orfType: payload.orfType || "uorf",
          customTranInput: payload.custom_tran_input || defaultTranscript || "",
          singleNucTran: payload.single_nuc_tran || defaultTranscript || "",
          principal: Boolean(payload.principal),
        });
      };

      handleShortCode(shortCodePayload, originalShortCode, customHandler);
    }
  }, [
    location.state?.shortCodePayload,
    location.state?.isFromShortCode,
    userPassedShortCode,
    defaultTranscript,
  ]);

  return { userPassedShortCode, consumeUserPassedShortCode, autoRunPlot, setAutoRunPlot };
};

/**
 * DifferentialExpression specific short code handler
 */
export const useDifferentialExpressionShortCode = ({
  location,
  updateMultipleSettings,
  setGroups,
}) => {
  const { userPassedShortCode, 
          consumeUserPassedShortCode, 
          autoRunPlot, 
          setAutoRunPlot, 
          handleShortCode } =
    useBaseShortCodeHandler({
      location,
      updateMultipleSettings,
      setSelectedFiles: () => {}, // Not used for group-based analysis
      pageName: "DifferentialExpression",
    });

  useEffect(() => {
    const shortCodePayload = location.state?.shortCodePayload;
    const originalShortCode = location.state?.shortCode;

    // Only process if we have a short code payload and haven't processed it yet
    if (
      shortCodePayload &&
      location.state?.isFromShortCode &&
      !userPassedShortCode
    ) {
      const customHandler = (payload) => {
        // DifferentialExpression specific field mapping
        updateMultipleSettings({
          geneType: payload.genetype || "coding",
          maxPadj: payload.minzscore || 0,
          riboRegion: payload.ribo_region || "fiveprime",
          rnaRegion: payload.rna_region || "fiveprime",
          xMin:
            payload.xmin !== "" && !isNaN(parseFloat(payload.xmin))
              ? String(payload.xmin)
              : "",
          xMax:
            payload.xmax !== "" && !isNaN(parseFloat(payload.xmax))
              ? String(payload.xmax)
              : "",
          yMin:
            payload.ymin !== "" && !isNaN(parseFloat(payload.ymin))
              ? String(payload.ymin)
              : "",
          yMax:
            payload.ymax !== "" && !isNaN(parseFloat(payload.ymax))
              ? String(payload.ymax)
              : "",
          batchPlotType: payload.batchPlotType || "plot_te"
        });

        // Handle groups from master_file_dict
        if (payload.master_file_dict && setGroups) {
          console.log(
            "Processing master_file_dict for DifferentialExpression:",
            payload.master_file_dict
          );

          setGroups((prevGroups) =>
            prevGroups.map((group) => {
              const groupData = payload.master_file_dict[group.id];
              if (
                groupData &&
                groupData.file_ids &&
                Array.isArray(groupData.file_ids)
              ) {
                const files = groupData.file_ids.map((id) => parseInt(id, 10));
                return { ...group, files };
              }
              return group;
            })
          );

          // Update condition names from payload if available
          if (payload.cond1_name || payload.cond2_name) {
            setGroups((prevGroups) =>
              prevGroups.map((group) => {
                if (group.id === "riboseq1" && payload.cond1_name) {
                  return { ...group, name: `Ribo-Seq ${payload.cond1_name}` };
                }
                if (group.id === "riboseq2" && payload.cond2_name) {
                  return { ...group, name: `Ribo-Seq ${payload.cond2_name}` };
                }
                if (group.id === "rnaseq1" && payload.cond1_name) {
                  return { ...group, name: `RNA-Seq ${payload.cond1_name}` };
                }
                if (group.id === "rnaseq2" && payload.cond2_name) {
                  return { ...group, name: `RNA-Seq ${payload.cond2_name}` };
                }
                return group;
              })
            );
          }
        }
      };

      handleShortCode(shortCodePayload, originalShortCode, customHandler);
    }
  }, [
    location.state?.shortCodePayload,
    location.state?.isFromShortCode,
    userPassedShortCode,
  ]);

  return { userPassedShortCode, consumeUserPassedShortCode, autoRunPlot, setAutoRunPlot };
};

/**
 * Generic payload creator that respects each plot's unique structure
 */
export const createPlotSpecificPayload = {
  subcodonProfile: ({
    settings,
    selectedFiles,
    projectId,
    userPassedShortCode,
  }) => ({
    // Subcodon Profile uses simple structure
    project_id: projectId,
    file_list: selectedFiles,
    user_passed_short_code: userPassedShortCode,
    cov_check: settings.riboSeqCoverage || false,
    hili_start: settings.highlightStart?.toString() || "",
    hili_stop: settings.highlightStop?.toString() || "",
    maxread: settings.maxRead || 150,
    minread: settings.minRead || 20,
    primetype: settings.offsetDirection || "fiveprime",
    readscore: settings.minTripletScore?.toString() || "0",
    seqhili: settings.highlightSequences || "",
    transcript: settings.transcript || "",
    plot_url: settings.plot_url || "/interactive_plot/",
  }),

  rrts: ({
    settings,
    selectedFiles,
    projectId,
    userPassedShortCode,
    groups,
    analysisMode,
    apiData,
  }) => {
    // RRTS uses complex master_file_dict structure
    const buildMaster = () => {
      if (analysisMode === "single") {
        return {
          group1: {
            file_ids: selectedFiles.map((id) => String(id)),
            file_names: selectedFiles
              .map((fileId) => {
                const file = apiData.json_files.find(
                  (f) => f.file_id === fileId
                );
                return file ? String(file.file_name) : "";
              })
              .filter(Boolean),
          },
        };
      } else {
        const grp1_name = groups[0].name
        // Replace any spaces/tabs/newlines with an underscore
        const clean_grp1_name = grp1_name.replace(/\s/g, "_");
        const grp2_name = groups[1].name
        // Replace any spaces/tabs/newlines with an underscore
        const clean_grp2_name = grp2_name.replace(/\s/g, "_");
        return {
          [clean_grp1_name]: {
            file_ids: (groups[0]?.files || []).map((id) => String(id)),
            file_names: (groups[0]?.files || [])
              .map((fileId) => {
                const file = apiData.json_files.find(
                  (f) => f.file_id === fileId
                );
                return file ? String(file.file_name) : "";
              })
              .filter(Boolean),
          },
          [clean_grp2_name]: {
            file_ids: (groups[1]?.files || []).map((id) => String(id)),
            file_names: (groups[1]?.files || [])
              .map((fileId) => {
                const file = apiData.json_files.find(
                  (f) => f.file_id === fileId
                );
                return file ? String(file.file_name) : "";
              })
              .filter(Boolean),
          },
        };
      }
    };

    return {
      project_id: String(projectId),
      analysis_type: String(analysisMode),
      user_passed_short_code: userPassedShortCode,
      master_file_dict: buildMaster(),

      // RRTS specific fields
      ambig_check: Boolean(settings.ambiguousReads || false),
      tran_list: String(settings.tranList || "prin_trans"),
      custom_tran_list: String(settings.customTranList || ""),
      exclude_rt_stop_check: Boolean(settings.excludeRtStop || false),
      min_rt_count: Number(settings.minRtCount) || 5,
      context_before: settings.contextBefore || "",
      context_after: settings.contextAfter || "",
      uaa_check: Boolean(settings.uaaCheck || false),
      uag_check: Boolean(settings.uagCheck || false),
      uga_check: Boolean(settings.ugaCheck || false),
      min_cds_density: Number(settings.minCdsDensity) || 50,
      min_cds_coverage: Number(settings.minCdsCoverage) || 0.5,
      minread: Number(settings.minReadLength) || 20,
      maxread: Number(settings.maxReadLength) || 150,
      cond1_name: String(groups[0]?.name || "Group1"),
      cond2_name: String(groups[1]?.name || "Group2"),
      email: Boolean(settings.emailNotification || false),
      plot_url: String("/rrts"),
    };
  },

  dataQuality: ({
    settings,
    selectedFiles,
    projectId,
    userPassedShortCode,
    apiData,
  }) => ({
    // Data Quality uses file_list directly
    project_id: projectId,
    file_list: selectedFiles,
    user_passed_short_code: userPassedShortCode,
    organism: apiData.organism || settings.organism,
    transcriptome: apiData.transcriptome || settings.transcriptome,
    all_seq_types: Array.isArray(apiData.seq_types)
      ? apiData.seq_types.join(",")
      : apiData.seq_types || "",

    // Data Quality specific fields
    plottype: settings.plottype,
    minread: parseInt(settings.minread) || 20,
    maxread: parseInt(settings.maxread) || 35,
    max_y_val: parseInt(settings.max_y_val) || "",
    minread_count: parseInt(settings.minread_count) || 10,
    min_cust_metagene_search_limit: parseInt(settings.min_cust_metagene_search_limit) || 0,
    max_cust_metagene_search_limit: parseInt(settings.max_cust_metagene_search_limit) || 0,
    min_limit_anchor: settings.min_limit_anchor || "cds_start",
    max_limit_anchor: settings.max_limit_anchor || "cds_start",
    hmap_strt: parseInt(settings.hmap_strt) || -50,
    hmap_stop: parseInt(settings.hmap_stop) || 20,
    maxscaleval: parseInt(settings.maxscaleval) || "None",
    custom_seq_list: settings.custom_seq_list || "",
    metagene_tranlist: settings.metagene_tranlist || "",
    te_genelist: settings.te_genelist || "",
    log_scale: Boolean(settings.log_scale),
    reverse: Boolean(settings.reverse),
    apply_offset: Boolean(settings.apply_offset),
    per: Boolean(settings.per),
    ambig: Boolean(settings.ambig),
    single_plt: Boolean(settings.single_plt),
    aggregate_counts: Boolean(settings.aggregate_counts),
    custom_search_region: settings.custom_search_region || "whole_gene",
    nuc_comp_type: settings.nuc_comp_type || "per",
    direction: settings.direction || "fiveprime",
    nuccomp_reads: settings.nuccomp_reads || "mapped",
    center_around: settings.center_around || "start_codons",
    color_palette: settings.color_palette || "gist_rainbow",
    norm_method: settings.norm_method || "average_read_density",
    count_type: settings.count_type || "gene_counts",
    mrna_dist_category: settings.mrna_dist_category || "mrna_dist_file",
    corr_type: settings.corr_type || "pearson",
    count_result: settings.count_result || "raw_count",
    plot_url: settings.plot_url || "/metainfo_plot/",
  }),

  batchSubcodonProfile: ({
    settings,
    selectedFiles,
    projectId,
    userPassedShortCode,
    batchSettings,
  }) => ({
    // Batch Subcodon Profile uses similar structure to regular subcodon profile
    project_id: projectId,
    file_list: selectedFiles,
    user_passed_short_code: userPassedShortCode,
    cov_check: settings.riboSeqCoverage || false,
    hili_start: settings.highlightStart?.toString() || "",
    hili_stop: settings.highlightStop?.toString() || "",
    maxread: settings.maxRead || 150,
    minread: settings.minRead || 20,
    primetype: settings.offsetDirection || "fiveprime",
    readscore: settings.minTripletScore?.toString() || "0",
    seqhili: settings.highlightSequences || "",
    transcript: settings.transcript || "",
    email_notification: Boolean(settings.email),

    // Batch-specific fields
    csv_check: batchSettings?.csvCheck || false,
    html_check: batchSettings?.htmlCheck || false,
    transcript_list: batchSettings?.transcriptList || "",
    ambig_check: settings.ambig_check || false,
    plot_url: "/batch_profiles/",
  }),

  comparisonProfile: ({
    settings,
    projectId,
    userPassedShortCode,
    masterFileDict,
  }) => ({
    // Comparison Profile specific payload structure
    ambig_check: settings.ambig_check || false,
    cov_check: settings.riboSeqCoverage || false,
    cumsum_check: settings.cumsum_check || false,
    hili_start: settings.highlightStart?.toString() || "0",
    hili_stop: settings.highlightStop?.toString() || "0",
    master_file_dict: masterFileDict,
    max_diff_pos: settings.max_diff_pos || null,
    maxread: settings.maxRead || 150,
    minread: settings.minRead || 20,
    norm_type: settings.normalizationType || "by_type",
    plot_url: "/comparison/",
    project_id: projectId,
    transcript: settings.transcript || "",
    user_passed_short_code: userPassedShortCode,
  }),

  batchComparisonProfile: ({
    settings,
    projectId,
    userPassedShortCode,
    masterFileDict,
    batchSettings,
  }) => ({
    // Batch Comparison Profile specific payload structure
    ambig_check: settings.ambig_check || false,
    cov_check: settings.riboSeqCoverage || false,
    csv_check: batchSettings?.csvCheck || false,
    html_check: batchSettings?.htmlCheck || false,
    master_file_dict: masterFileDict,
    maxread: settings.maxRead || 150,
    minread: settings.minRead || 20,
    norm_check: settings.norm_check || false,
    email_notification: Boolean(settings.email),
    plot_url: "/batch_comparison_profiles/",
    project_id: projectId,
    transcript_list: batchSettings?.transcriptList || "",
    user_passed_short_code: userPassedShortCode,
  }),

  pauseDetection: ({
    settings,
    selectedFiles,
    projectId,
    userPassedShortCode,
    analysisMode,
    groups,
    buildMaster,
  }) => ({
    // Pause Detection specific payload structure
    project_id: String(projectId),
    analysis_type: String(analysisMode),
    user_passed_short_code: userPassedShortCode
      ? String(userPassedShortCode)
      : null,

    // Analysis parameters (matching the actual field names used)
    tran_list: String(settings.tran_list || "prin_trans"),
    custom_tran_list: String(settings.custom_tran_list || ""),
    min_fold_change: parseFloat(settings.min_fold_change) || 10,
    window_size: parseInt(settings.window_size) || 1000,
    min_coverage: parseFloat(settings.min_coverage) || 5,
    min_non_zero: parseFloat(settings.min_non_zero) || 20,
    nuc_output: parseFloat(settings.nuc_output) || 15,
    minread: parseInt(settings.minread) || 20,
    maxread: parseInt(settings.maxread) || 35,
    reproducible: Boolean(settings.reproducible),
    ambig_check: Boolean(settings.ambig_check),
    email: Boolean(settings.email),
    cond1_name: String(groups[0]?.name || "Group1"),
    cond2_name: String(groups[1]?.name || "Group2"),
    master_file_dict: buildMaster ? buildMaster() : {},

    plot_url: "/pause_detection/",
  }),

  translonExplorer: ({
    settings,
    selectedFiles,
    projectId,
    userPassedShortCode,
  }) => ({
    // Translon explorer specific payload structure
    project_id: String(projectId),
    plot_url: "/translon_explorer/",
    user_passed_short_code: userPassedShortCode,

    // Translon Explorer parameters
    region: String(settings.orfType || "uorf"),
    tran_input: String(settings.transcriptInput || "prin_trans"),
    custom_tran_input: String(settings.customTranscriptList || ""),

    // Start codons, back end expects T's instead of U's so replace here
    start_codons: (settings.startCodons || []).map(c => c.replace(/U/g, 'T')),

    // Filters
    min_prob: parseFloat(settings.filters?.minProbability) || 0,
    max_prob: parseFloat(settings.filters?.maxProbability) || 0,
    min_ard: parseFloat(settings.filters?.minAvgReadDensity) || 0,
    max_ard: parseFloat(settings.filters?.maxAvgReadDensity) || 0,
    min_cds: parseFloat(settings.filters?.minCds) || 0,
    max_cds: parseFloat(settings.filters?.maxCds) || 0,
    min_readlen: parseInt(settings.filters?.minReadLen) || 0,
    max_readlen: parseInt(settings.filters?.maxReadLen) || 0,
    min_len: parseInt(settings.filters?.minLen) || 0,
    max_len: parseInt(settings.filters?.maxLen) || 0,

    // Email and files
    email: Boolean(settings.emailNotification),
    file_list: selectedFiles.map(String),
  }),

  differentialSilhouette: ({
    settings,
    projectId,
    userPassedShortCode,
    groups,
    buildMasterFileDict,
  }) => ({
    // Differential Silhouette specific payload structure
    project_id: String(projectId),
    plot_url: "/differential_silhouette/",
    user_passed_short_code: userPassedShortCode,
    genetype: String(settings.geneType || "coding"),
    region: String(settings.region || "region_entire_tran"),
    minread: parseInt(settings.minReadLength) || 20,
    maxread: parseInt(settings.maxReadLength) || 150,
    dmax_threshold: parseFloat(settings.dMaxThreshold) || 0,
    dcorr_threshold: parseFloat(settings.dCorrThreshold) || 0,
    dcorr_min: parseFloat(settings.dCorrMin) || 0,
    ambig_check: Boolean(settings.ambiguousReads),
    email: Boolean(settings.emailNotification),
    cond1_name: String(groups[0]?.name || "Group1"),
    cond2_name: String(groups[1]?.name || "Group2"),
    master_file_dict: buildMasterFileDict ? buildMasterFileDict() : {},
  }),

  differentialCdsLeader: ({
    settings,
    projectId,
    userPassedShortCode,
    groups,
    buildMasterFileDict,
  }) => ({
    // Differential CDS Leader specific payload structure
    project_id: String(projectId),
    plot_url: "/diff_leader_cds/",
    user_passed_short_code: userPassedShortCode,
    minread: parseInt(settings.minread) ?? 20,
    maxread: parseInt(settings.maxread) ?? 150,
    ambig_check: Boolean(settings.ambig_check),
    email_notification: Boolean(settings.email),
    cond1_name: String(groups[0]?.name || "Group 1"),
    cond2_name: String(groups[1]?.name || "Group 2"),
    master_file_dict: buildMasterFileDict ? buildMasterFileDict() : {},
  }),

  codonOccupancy: ({
    settings,
    selectedFiles,
    projectId,
    userPassedShortCode,
    analysisMode,
    groups,
    buildMaster,
  }) => ({
    // Codon Occupancy specific payload structure
    project_id: String(projectId),
    plot_type: String(analysisMode),
    plot_url: "/codon_occupancy",
    user_passed_short_code: userPassedShortCode,
    group_type: String(settings.groupType || "codon"),
    norm_type: String(settings.normType || "average_read_density"),
    minread: parseInt(settings.minRead) || 20,
    maxread: parseInt(settings.maxRead) || 35,
    xmin: parseFloat(settings.xmin) || "",
    xmax: parseFloat(settings.xmax) || "",
    ymin: parseFloat(settings.ymin) || "",
    ymax: parseFloat(settings.ymax) || "",
    cond1_name: String(groups[0]?.name || "Group1"),
    cond2_name:
      analysisMode === "comparison" ? String(groups[1]?.name || "Group2") : "",
    email: Boolean(settings.emailNotification),
    tran_list: String(settings.tranList || ""),
    master_file_dict: buildMaster ? buildMaster() : {},
  }),

  utrPeriodicity: ({
    settings,
    projectId,
    userPassedShortCode,
    buildMaster,
  }) => ({
    // UTR Periodicity specific payload structure
    project_id: String(projectId),
    plot_url: "/utr_periodicity/",
    custom_tran_list: String(settings.customTranList || ""),
    email: Boolean(settings.email),
    exclude_rt_stop: Boolean(settings.excludeRtStop),
    master_file_dict: buildMaster ? buildMaster() : {},
    max_y_val: settings.maxYVal ? Number(settings.maxYVal) : null,
    maxread: Number(settings.maxread) || 150,
    minread: Number(settings.minread) || 20,
    select_rt_only: Boolean(settings.selectRtOnly),
    tran_selection: String(settings.tranSelection || "prin_trans"),
    user_passed_short_code: userPassedShortCode
      ? String(userPassedShortCode)
      : null,
  }),

  terminationMetagene: ({
    settings,
    projectId,
    userPassedShortCode,
    buildMaster,
  }) => ({
    // Termination Metagene specific payload structure
    project_id: String(projectId),
    plot_url: "/termination_metagene/",
    custom_tran_list: String(settings.customTranList || ""),
    downstream_selection: String(settings.downstreamSelection || "downstream_3utr"),
    email: Boolean(settings.email),
    master_file_dict: buildMaster ? buildMaster() : {},
    maxread: Number(settings.maxread) || 150,
    min_count: Number(settings.minCount) || 10,
    min_utr: Number(settings.minUtr) || 50,
    minread: Number(settings.minread) || 20,
    normalise_method: String(settings.normaliseMethod || "ard"),
    tran_selection: String(settings.tranSelection || "prin_trans"),
    user_passed_short_code: userPassedShortCode
      ? String(userPassedShortCode)
      : null,
  }),

  timeSeries: ({
    settings,
    projectId,
    userPassedShortCode,
    getMasterFileDict,
  }) => ({
    // TimeSeries specific payload structure
    project_id: String(projectId),
    genetype: String(settings.geneType || "all"),
    gene_no: settings.geneNo ? parseInt(settings.geneNo, 10) : 50,
    analysis_type: String(settings.analysisType || "single_control"),
    ribo_region: String(settings.riboRegion || "all"),
    rna_region: String(settings.rnaRegion || "all"),
    selected_seq_type: String(settings.selectedSeqType || "TE"),
    custom_control_name: String(settings.customControlName || "Control"),
    custom_treatment_name: String(settings.customTreatmentName || "Treatment"),
    col_min: settings.colMin ? parseFloat(settings.colMin) : "",
    col_max: settings.colMax ? parseFloat(settings.colMax) : "",
    custom_gene_list: String(settings.customGeneList || ""),
    email: Boolean(settings.emailNotification),
    user_passed_short_code: userPassedShortCode
      ? String(userPassedShortCode)
      : null,
    plot_url: "/time_series/",
    master_file_dict: getMasterFileDict ? getMasterFileDict() : {},
  }),

  polyShift: ({
    settings,
    projectId,
    userPassedShortCode,
    getMasterFileDict,
  }) => ({
    // PolyShift specific payload structure
    project_id: String(projectId),
    plot_type: String(settings.plotType || "interactive"),
    genetype: String(settings.geneType || "all"),
    analysis_type: "control", // PolyShift always uses control vs treatment
    selected_color_palette: String(settings.colorPalette || "orange_purple"),
    gene_no: settings.geneNo ? parseInt(settings.geneNo, 10) : 50,
    custom_control_name: String(settings.customControlName || "Control"),
    custom_treatment_name: String(settings.customTreatmentName || "Treatment"),
    custom_transcript_list: String(settings.customTranscriptList || ""),
    zscore_check: Boolean(settings.zscoreCheck || false),
    only_sig_check: Boolean(settings.onlySigCheck || false),
    col_min: settings.colMin ? parseFloat(settings.colMin) : "",
    col_max: settings.colMax ? parseFloat(settings.colMax) : "",
    email: Boolean(settings.emailNotification || false),
    plot_url: "/poly_shift",
    user_passed_short_code: userPassedShortCode
      ? String(userPassedShortCode)
      : null,
    master_file_dict: getMasterFileDict ? getMasterFileDict() : {},
  }),

  transcriptomeInfo: ({ settings, projectId, userPassedShortCode }) => ({
    // TranscriptomeInfo specific payload structure
    project_id: String(projectId),
    plot_url: "/traninfo_plot",
    user_passed_short_code: userPassedShortCode,
    plottype: String(settings.plotType || "fetch_seq"),
    gc_location: String(settings.gcLocation || "all"),
    orftype: String(settings.orfType || "uorf"),
    min_limit_anchor: String(settings.min_limit_anchor || "tx_start"),
    max_limit_anchor: String(settings.max_limit_anchor || "tx_end"),
    min_limit_val: parseInt(settings.min_limit_val) || 0,
    max_limit_val: parseInt(settings.max_limit_val) || 0,
    custom_tran_input: String(settings.customTranInput || ""),
    single_nuc_tran: String(settings.singleNucTran || ""),
    principal: Boolean(settings.principal),
    transcriptInput: String(settings.transcriptInput || "prin_trans"),
  }),

  differentialExpression: ({
    settings,
    projectId,
    userPassedShortCode,
    groups,
    getMasterFileDict,
    apiData,
  }) => {
    // Extract condition names from Ribo-Seq group labels
    const riboGroup1 = groups.find((g) => g.id === "riboseq1");
    const riboGroup2 = groups.find((g) => g.id === "riboseq2");

    // Extract condition names by removing 'Ribo-Seq' prefix
    const cond1Name =
      riboGroup1?.name?.replace(/^Ribo-Seq\s*/i, "").trim() || "Condition 1";
    const cond2Name =
      riboGroup2?.name?.replace(/^Ribo-Seq\s*/i, "").trim() || "Condition 2";

    return {
      // DifferentialExpression specific payload structure
      project_id: projectId,
      cond1_name: cond1Name,
      cond2_name: cond2Name,
      genetype: settings.geneType || "coding",
      master_file_dict: getMasterFileDict
        ? getMasterFileDict(apiData?.json_files || [])
        : {},
      minzscore: parseFloat(settings.maxPadj) || 0,
      plot_url: "/differential/",
      ribo_region: settings.riboRegion || "fiveprime",
      rna_region: settings.rnaRegion || "fiveprime",
      user_passed_short_code: userPassedShortCode,
      email_notification: Boolean(settings.email) || false,
      // Always include axis values, use empty string for invalid values
      xmin:
        settings.xMin !== "" && !isNaN(parseFloat(settings.xMin))
          ? parseFloat(settings.xMin)
          : "",
      xmax:
        settings.xMax !== "" && !isNaN(parseFloat(settings.xMax))
          ? parseFloat(settings.xMax)
          : "",
      ymin:
        settings.yMin !== "" && !isNaN(parseFloat(settings.yMin))
          ? parseFloat(settings.yMin)
          : "",
      ymax:
        settings.yMax !== "" && !isNaN(parseFloat(settings.yMax))
          ? parseFloat(settings.yMax)
          : "",
    };
  },
};
